import React, { useCallback, useState, useEffect } from 'react';
import { Row } from '@thd-olt-component-react/core-ui';
import {
  useDataModel
} from '@thd-nucleus/data-sources';
import {
  Tile,
  TileGroup,
  TileMedia,
  Typography,
} from '@one-thd/sui-atomic-components';
import _isEmpty from 'lodash/isEmpty';
import './price.scss';
import PropTypes from 'prop-types';
import { useCabinetVisualizerStore } from '../../../../../store';
import { useDrawerStore } from '../../../../../store/ui';
import logNewRelicCustom from '../../../../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../../../../constants';

const splitSSKUAttributes = (attributeString, attributeList) => {
  return attributeString?.split('~~')?.map((value, index) => ({
    name: attributeList[index].attributeName,
    value
  }));
};

const getWidthHeightStyles = (isSelected) => {
  let styles = {
    cursor: 'pointer',
    display: 'flex',
    height: 'auto',
    justifyContent: 'center',
    minWidth: '25px',
    padding: '8px 10px',
    borderRadius: '3px'
  };
  return { ...styles };
};

const getColorOrFinishTileStyle = (isSelected) => {
  let styles = {
    padding: '3px',
    borderRadius: '3px'
  };
  return { ...styles };
};

export const SSKUSelector = ({ editMode, setSSKUDataLoading }) => {
  const [availableSkus, setAvailableSkus] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const { updateCabinetVisualizer } = useCabinetVisualizerStore((state) => state);
  const {
    styleChoiceId,
    skuDrawer: {
      superSkuProducts,
      selectedParentId,
      selectedItemId
    }
  } = useCabinetVisualizerStore((state) => state.cabinetVisualizer);

  const cabinetVisualizer = useCabinetVisualizerStore((state) => state.cabinetVisualizer);
  const skuDrawer = cabinetVisualizer.skuDrawer;

  const selectedCollection = useDrawerStore((state) => state.selectedCollection);
  const collectionColorFinish = selectedCollection?.colorFinish;

  const currentSSKU = superSkuProducts.find((sku) => sku.parentId === selectedParentId);

  const superSkuResponse = useDataModel('metadata', {
    variables: { parentId: currentSSKU.parentId },
    ssr: false,
    skip: currentSSKU.parentId === currentSSKU.itemId
  });

  const getSelectedItemByCollectionColorFinish = (childItemList) => {
    if (!collectionColorFinish) return null;

    const selectedItem = childItemList.find((item) => item.attributes
      .find((attr) => attr.name === 'Color/Finish' && attr.value === collectionColorFinish));
    if (selectedItem && Number(skuDrawer.selectedItemId) !== Number(selectedItem.itemId)) {
      updateCabinetVisualizer({
        ...cabinetVisualizer,
        skuDrawer: {
          ...skuDrawer,
          selectedItemId: Number(selectedItem.itemId),
        }
      });
    }
    return selectedItem;
  };

  useEffect(() => {
    setSSKUDataLoading(superSkuResponse.loading);
    if (!superSkuResponse.loading && !_isEmpty(superSkuResponse.data) && !_isEmpty(superSkuResponse.data.metadata)) {
      const metadataAttributes = superSkuResponse?.data?.metadata?.attributes;
      const childItems = superSkuResponse?.data?.metadata?.childItemsLookup;

      if (childItems && metadataAttributes) {
        setAttributes(metadataAttributes);
        const childItemList = childItems.map((childItem) => ({
          itemId: Number(childItem.itemId),
          attributes: splitSSKUAttributes(childItem.attributeCombination, metadataAttributes),
          attributeCombination: childItem.attributeCombination
        }));
        setAvailableSkus(childItemList);

        let selectedItem = childItems.find((item) => item.itemId === String(selectedItemId));
        if (collectionColorFinish && !editMode) {
          selectedItem = getSelectedItemByCollectionColorFinish(childItemList) || selectedItem;
        }

        if (selectedItem) {
          setSelectedChoices(splitSSKUAttributes(selectedItem.attributeCombination, metadataAttributes));
        } else {
          setSelectedChoices(splitSSKUAttributes(childItems[0].attributeCombination, metadataAttributes));
          updateCabinetVisualizer({
            skuDrawer: {
              selectedItemId: Number(childItems[0].itemId),
            }
          });
        }
      }
    }
  }, [superSkuResponse]);

  const findVariantSku = useCallback((name, value) => {
    let attributeString = '';
    selectedChoices.forEach((attribute) => {
      if (attributeString) {
        attributeString += '~~';
      }
      attributeString += attribute.name !== name ? attribute.value : value;
    });

    return availableSkus.find((sku) => sku.attributeCombination === attributeString);
  }, [availableSkus, selectedChoices]);

  const findPartialVariantSku = useCallback((name, value) => {
    let removalIndex = selectedChoices.length;
    let variant = null;
    let valueIndex = 0;
    const variantSelections = selectedChoices.map((attribute, index) => {
      if (attribute.name === name) {
        valueIndex = index;
        return value;
      }
      return attribute.value;
    });

    while (!variant && removalIndex >= 0) {
      removalIndex -= 1;
      if (removalIndex !== valueIndex) {
        variantSelections[removalIndex] = '.*';
      }
      const searchKey = new RegExp(`^${variantSelections.join('~~')}$`);
      variant = availableSkus.find((sku) => searchKey.test(sku.attributeCombination));
    }
    return variant;
  }, [availableSkus, selectedChoices]);

  const selectChoice = (name, value) => {
    const newSKU = findVariantSku(name, value) || findPartialVariantSku(name, value);

    if (!newSKU) {
      return;
    }

    setSelectedChoices(splitSSKUAttributes(newSKU.attributeCombination, attributes));
    updateCabinetVisualizer({
      skuDrawer: {
        selectedItemId: newSKU.itemId,
      }
    });
  };

  return (
    <>
      {attributes.length > 0 && attributes.map((attribute, index) => (
        <Row key={attribute.attributeName}>
          <div className="sui-pt-2 sui-w-full">
            {attribute.attributeValues.length > 1 && (
              <>
                <Typography variant="body-small" color="primary">
                  {`${attribute.attributeName}: `}
                  <b>{selectedChoices[index].value}</b>
                </Typography>
                <div className="sui-pt-1">
                  <TileGroup
                    value={selectedChoices[index].value}
                    onChange={(event, newValue) => selectChoice(attribute.attributeName, newValue)}
                    aria-label={`${attribute.attributeName} Options`}
                  >
                    {attribute.attributeValues.map((attributeValue) => {
                      const isSelected = attributeValue.value === selectedChoices[index].value;
                      const isUnavailable = !findVariantSku(attribute.attributeName, attributeValue.value);
                      return (
                        <Tile
                          key={`${attributeValue.value}-key`}
                          value={attributeValue.value}
                          aria-label={attributeValue.value}
                          style={attribute.isSwatch
                            ? getColorOrFinishTileStyle(isSelected, isUnavailable)
                            : getWidthHeightStyles(isSelected, isUnavailable)}
                          unavailable={isUnavailable}
                        >
                          {attribute.isSwatch
                            ? (<TileMedia src={attributeValue.swatchGuid} style={{ padding: '0px' }} />)
                            : attributeValue.value}
                        </Tile>
                      );
                    })}
                  </TileGroup>
                </div>
              </>
            )}
          </div>
        </Row>
      ))}
    </>
  );
};

SSKUSelector.propTypes = {
  setSSKUDataLoading: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
};
